import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Description as DocIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { canViewLucros, ROLES } from '../../utils/permissions';

export default function Vendas() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [vendas, setVendas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    cliente_id: '',
    veiculo_id: '',
    valor_venda: '',
    forma_pagamento: 'avista',
    observacoes: '',
    vendedor_id: '',
    comissao_percentual: '',
  });
  const [custoInfo, setCustoInfo] = useState(null);
  const [veiculoSelecionado, setVeiculoSelecionado] = useState(null);
  const podeVerLucros = canViewLucros(usuario);
  const isAdmin = usuario?.nivel_acesso === ROLES.ADMIN;

  const loadVendas = async () => {
    try {
      const response = await api.get('/vendas');
      setVendas(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar vendas:', error);
      toast.error('Erro ao carregar vendas');
    }
  };

  const handleDeleteVenda = async (venda) => {
    if (!window.confirm(`Excluir a venda de "${venda.veiculo?.marca} ${venda.veiculo?.modelo}" para "${venda.cliente?.nome}"?\nO veículo voltará ao estoque como disponível.`)) return;
    try {
      await api.delete(`/vendas/${venda.id}`);
      toast.success('Venda excluída. Veículo devolvido ao estoque.');
      loadVendas();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir venda');
    }
  };

  const loadClientes = async () => {
    try {
      const response = await api.get('/clientes', { params: { limit: 1000 } });
      const data = response.data.clientes || response.data || [];
      setClientes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const loadVeiculos = async () => {
    try {
      const response = await api.get('/veiculos', { params: { limit: 1000, status: 'disponivel' } });
      const data = response.data.veiculos || response.data || [];
      setVeiculos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar veiculos:', error);
    }
  };

  const loadVendedores = async () => {
    try {
      const response = await api.get('/usuarios');
      const data = response.data;
      const lista = Array.isArray(data) ? data : [];
      setVendedores(lista.filter((u) => u.ativo !== false));
    } catch (error) {
      console.error('Erro ao carregar vendedores:', error);
    }
  };

  useEffect(() => {
    loadVendas();
    loadClientes();
    loadVeiculos();
    loadVendedores();
  }, []);

  const handleOpen = () => {
    setFormData({
      cliente_id: '',
      veiculo_id: '',
      valor_venda: '',
      forma_pagamento: 'avista',
      observacoes: '',
      vendedor_id: '',
      comissao_percentual: '',
    });
    setCustoInfo(null);
    setVeiculoSelecionado(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCustoInfo(null);
    setVeiculoSelecionado(null);
  };

  const handleSelectVeiculo = async (veiculoId) => {
    if (!veiculoId) {
      setFormData((prev) => ({ ...prev, veiculo_id: '', valor_venda: '' }));
      setCustoInfo(null);
      setVeiculoSelecionado(null);
      return;
    }

    // Buscar veiculo selecionado
    const veiculo = veiculos.find((v) => v.id === parseInt(veiculoId));
    setVeiculoSelecionado(veiculo);

    // Preencher valor de venda com preco_venda do veiculo
    const precoVenda = veiculo.preco_venda || '';
    setFormData((prev) => ({
      ...prev,
      veiculo_id: veiculoId,
      valor_venda: precoVenda ? parseFloat(precoVenda).toFixed(2) : '',
    }));

    // Buscar custo total
    try {
      const response = await api.get(`/veiculos/${veiculoId}/custo-total`);
      setCustoInfo(response.data);
    } catch (error) {
      console.error('Erro ao calcular custo:', error);
      setCustoInfo(null);
    }
  };

  const calcularLucroEstimado = () => {
    if (!custoInfo || !formData.valor_venda) return null;
    const valorVenda = parseFloat(formData.valor_venda) || 0;
    const custoTotal = parseFloat(custoInfo.custo_total) || 0;
    return (valorVenda - custoTotal).toFixed(2);
  };

  const calcularComissaoValor = () => {
    const pct = parseFloat(formData.comissao_percentual) || 0;
    const val = parseFloat(formData.valor_venda) || 0;
    if (!pct || !val) return null;
    return (val * (pct / 100)).toFixed(2);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.cliente_id || !formData.veiculo_id || !formData.valor_venda) {
        toast.error('Preencha cliente, veículo e valor da venda');
        return;
      }

      const response = await api.post('/vendas', {
        ...formData,
        valor_venda: parseFloat(formData.valor_venda),
        vendedor_id: formData.vendedor_id || null,
        comissao_percentual: parseFloat(formData.comissao_percentual) || 0,
      });

      toast.success('Venda realizada com sucesso!');
      handleClose();
      loadVendas();
      loadVeiculos();

      // Redirecionar para pagina de documentos com dados da venda
      const vendaId = response.data.id;
      if (vendaId) {
        navigate('/documentos', { state: { vendaId, gerarDocumento: true } });
      }
    } catch (error) {
      console.error('Erro ao realizar venda:', error);
      toast.error(error.response.data.error || 'Erro ao realizar venda');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const lucroEstimado = calcularLucroEstimado();
  const comissaoValor = calcularComissaoValor();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Vendas</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
          Realizar Venda
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>Veículo</TableCell>
              <TableCell>Placa</TableCell>
              <TableCell>Vendedor</TableCell>
              <TableCell>Valor</TableCell>
              {podeVerLucros && <TableCell>Comissão</TableCell>}
              {podeVerLucros && <TableCell>Lucro</TableCell>}
              <TableCell>Data</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vendas.map((venda) => (
              <TableRow key={venda.id}>
                <TableCell>{venda.cliente?.nome || '-'}</TableCell>
                <TableCell>{`${venda.veiculo?.marca || ''} ${venda.veiculo?.modelo || ''}`}</TableCell>
                <TableCell>{venda.veiculo?.placa || '-'}</TableCell>
                <TableCell>{venda.vendedor?.nome || '-'}</TableCell>
                <TableCell>{formatCurrency(venda.valor_venda)}</TableCell>
                {podeVerLucros && (
                  <TableCell>
                    {venda.comissao_percentual > 0 ? (
                      <Chip
                        label={`${formatCurrency(venda.comissao_valor)} (${parseFloat(venda.comissao_percentual).toFixed(1)}%)`}
                        color="warning"
                        size="small"
                      />
                    ) : '-'}
                  </TableCell>
                )}
                {podeVerLucros && (
                  <TableCell>
                    <Chip
                      label={formatCurrency(venda.lucro_liquido)}
                      color={parseFloat(venda.lucro_liquido) >= 0 ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                )}
                <TableCell>{new Date(venda.data_venda).toLocaleDateString('pt-BR')}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    startIcon={<DocIcon />}
                    onClick={() => navigate('/documentos', { state: { vendaId: venda.id } })}
                  >
                    Documentos
                  </Button>
                  {isAdmin && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteVenda(venda)}
                      sx={{ ml: 1 }}
                    >
                      Excluir
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {vendas.length === 0 && (
              <TableRow>
                <TableCell colSpan={podeVerLucros ? 9 : 7} align="center">
                  Nenhuma venda registrada
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Realizar Venda</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Cliente"
                value={formData.cliente_id}
                onChange={(e) => setFormData({ ...formData, cliente_id: e.target.value })}
              >
                <MenuItem value="">Selecione um cliente</MenuItem>
                {clientes.map((cliente) => (
                  <MenuItem key={cliente.id} value={cliente.id}>
                    {cliente.nome} - {cliente.cpf_cnpj}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Veículo"
                value={formData.veiculo_id}
                onChange={(e) => handleSelectVeiculo(e.target.value)}
              >
                <MenuItem value="">Selecione um veículo</MenuItem>
                {veiculos.map((veiculo) => (
                  <MenuItem key={veiculo.id} value={veiculo.id}>
                    {`${veiculo.marca} ${veiculo.modelo} - ${veiculo.placa} - ${formatCurrency(veiculo.preco_venda)}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Valor da Venda (R$)"
                type="number"
                value={formData.valor_venda}
                onChange={(e) => setFormData({ ...formData, valor_venda: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
                helperText={veiculoSelecionado ? `Preço sugerido: ${formatCurrency(veiculoSelecionado.preco_venda)}` : ''}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Forma de Pagamento"
                value={formData.forma_pagamento}
                onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
              >
                <MenuItem value="avista">À Vista</MenuItem>
                <MenuItem value="financiamento">Financiamento</MenuItem>
                <MenuItem value="consorcio">Consórcio</MenuItem>
                <MenuItem value="cartao">Cartão de Crédito</MenuItem>
                <MenuItem value="parcelado">Parcelado Loja</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Vendedor"
                value={formData.vendedor_id}
                onChange={(e) => setFormData({ ...formData, vendedor_id: e.target.value })}
              >
                <MenuItem value="">Nenhum</MenuItem>
                {vendedores.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.nome}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Comissão (%)"
                type="number"
                value={formData.comissao_percentual}
                onChange={(e) => setFormData({ ...formData, comissao_percentual: e.target.value })}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                disabled={!formData.vendedor_id}
                helperText={
                  comissaoValor && formData.vendedor_id
                    ? `Valor da comissão: ${formatCurrency(comissaoValor)}`
                    : 'Selecione um vendedor para calcular'
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={2}
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              />
            </Grid>

            {custoInfo && podeVerLucros && (
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Resumo Financeiro
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Preço de Compra
                      </Typography>
                      <Typography variant="body1">
                        {formatCurrency(custoInfo.preco_compra)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Manutenções
                      </Typography>
                      <Typography variant="body1">
                        {formatCurrency(custoInfo.total_manutencoes)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Custo Total
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" color="warning.main">
                        {formatCurrency(custoInfo.custo_total)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Lucro Estimado
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        color={parseFloat(lucroEstimado) >= 0 ? 'success.main' : 'error.main'}
                      >
                        {lucroEstimado ? formatCurrency(lucroEstimado) : '-'}
                      </Typography>
                    </Grid>
                    {comissaoValor && formData.vendedor_id && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" color="warning.main">
                          Comissão do vendedor ({formData.comissao_percentual}%): <strong>{formatCurrency(comissaoValor)}</strong>
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.cliente_id || !formData.veiculo_id || !formData.valor_venda}
          >
            Confirmar Venda
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


